// src/theme/theme.js
import { createTheme } from '@mui/material/styles';
import { colors, customColors } from './colorScheme';

// Create a theme instance with our EV-inspired color scheme
const theme = createTheme({
  palette: {
    primary: colors.primary,
    secondary: colors.secondary,
    error: colors.error,
    warning: colors.warning,
    info: colors.info,
    success: colors.success,
    background: colors.background,
    text: colors.text,
    divider: colors.divider,
  },
  typography: {
    fontFamily: [
      'Inter',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none', // Prevents all-caps buttons
    },
  },
  shape: {
    borderRadius: 6, // Slightly rounded corners
  },
  components: {
    // AppBar styling
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: customColors.headerBackground,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    // Drawer styling for sidebar
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: customColors.sidebarBackground,
          color: '#ffffff',
        },
      },
    },
    // ListItem styling (for sidebar)
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: `${customColors.activeNavItem}20`, // 20% opacity
            '&:hover': {
              backgroundColor: `${customColors.activeNavItem}30`, // 30% opacity on hover
            },
          },
        },
      },
    },
    // ListItemIcon styling for sidebar
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#ffffff',
        },
      },
    },
    // Card styling
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    // Chip styling (for statuses)
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
        colorSuccess: {
          backgroundColor: customColors.statusActive,
        },
        colorError: {
          backgroundColor: colors.error.main,
        },
        colorWarning: {
          backgroundColor: colors.warning.main,
        },
        colorInfo: {
          backgroundColor: colors.info.main,
        },
      },
    },
    // Button styling
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: 'none',
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          },
        },
        containedPrimary: {
          backgroundColor: colors.primary.main,
          '&:hover': {
            backgroundColor: colors.primary.dark,
          },
        },
        containedSecondary: {
          backgroundColor: colors.secondary.main,
          '&:hover': {
            backgroundColor: colors.secondary.dark,
          },
        },
        // Add an accent variant
        containedWarning: {
          backgroundColor: colors.accent.main,
          color: colors.accent.contrastText,
          '&:hover': {
            backgroundColor: colors.accent.dark,
          },
        },
      },
    },
    // Table styling
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: `${colors.background.default}`,
        },
      },
    },
    // Paper styling
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
        },
        elevation2: {
          boxShadow: '0 3px 6px rgba(0,0,0,0.1), 0 3px 6px rgba(0,0,0,0.05)',
        },
      },
    },
  },
});

export default theme;